import React, { useState, useRef } from 'react';
import { Search, Upload, FileText, CheckCircle, AlertCircle, Loader2, Send, User, Mail, Phone, Banknote, X, Check, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicFinancingPage() {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('reference'); // 'reference' or 'email'
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  // Found data state
  const [financingRequest, setFinancingRequest] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [devisInfo, setDevisInfo] = useState(null);
  
  // Upload state
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const fileInputRefs = useRef({});
  
  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Search for financing request
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Veuillez saisir une référence de devis ou un email');
      return;
    }
    
    setSearching(true);
    setSearchError('');
    setFinancingRequest(null);
    setClientInfo(null);
    setDevisInfo(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/financing/search?query=${encodeURIComponent(searchQuery.trim())}&type=${searchType}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Aucune demande de financement trouvée');
      }
      
      const data = await response.json();
      setFinancingRequest(data.financing_request);
      setClientInfo(data.client);
      setDevisInfo(data.devis);
      
      // Initialize uploaded docs state
      const docs = {};
      data.financing_request.documents?.forEach(doc => {
        if (doc.status === 'uploaded' || doc.status === 'validated') {
          docs[doc.doc_id] = true;
        }
      });
      setUploadedDocs(docs);
      
      toast.success('Dossier de financement trouvé');
    } catch (error) {
      setSearchError(error.message);
      toast.error(error.message);
    } finally {
      setSearching(false);
    }
  };

  // Handle file upload
  const handleUpload = async (docId, file) => {
    if (!financingRequest?.id) return;
    
    setUploadingDocId(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/public/financing/${financingRequest.id}/upload/${docId}`,
        {
          method: 'POST',
          body: formData,
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du téléversement');
      }
      
      // Update local state
      setUploadedDocs(prev => ({ ...prev, [docId]: true }));
      
      // Update the document in financing request
      setFinancingRequest(prev => ({
        ...prev,
        documents: prev.documents.map(doc =>
          doc.doc_id === docId
            ? { ...doc, status: 'uploaded', file_name: file.name }
            : doc
        ),
        progress: calculateProgress(prev.documents.map(doc =>
          doc.doc_id === docId ? { ...doc, status: 'uploaded' } : doc
        ))
      }));
      
      toast.success(`Document "${file.name}" téléversé avec succès`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingDocId(null);
    }
  };

  // Calculate progress
  const calculateProgress = (documents) => {
    if (!documents || documents.length === 0) return 0;
    const uploaded = documents.filter(d => d.status === 'uploaded' || d.status === 'validated').length;
    return Math.round((uploaded / documents.length) * 100);
  };

  // Check if all required documents are uploaded
  const allRequiredDocsUploaded = () => {
    if (!financingRequest?.documents) return false;
    const requiredDocs = financingRequest.documents.filter(d => d.required);
    return requiredDocs.every(d => d.status === 'uploaded' || d.status === 'validated');
  };

  // Submit completed dossier
  const handleSubmitDossier = async () => {
    if (!financingRequest?.id || !allRequiredDocsUploaded()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/financing/${financingRequest.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientInfo?.name,
          client_email: clientInfo?.email,
          devis_reference: devisInfo?.reference
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la soumission');
      }
      
      setSubmitted(true);
      
      // Update financing request status
      setFinancingRequest(prev => ({
        ...prev,
        status: 'dossier_complet'
      }));
      
      toast.success('Votre dossier a été soumis avec succès ! Notre équipe va le traiter rapidement.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Get status info
  const getStatusInfo = (status) => {
    const statuses = {
      pending: { label: 'En attente', color: 'bg-slate-100 text-slate-600', icon: HelpCircle },
      uploaded: { label: 'Téléversé', color: 'bg-blue-100 text-blue-700', icon: Upload },
      validated: { label: 'Validé', color: 'bg-green-100 text-green-700', icon: Check },
      rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700', icon: X },
    };
    return statuses[status] || statuses.pending;
  };

  const progress = financingRequest?.progress || calculateProgress(financingRequest?.documents);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Banknote className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            DÉPÔT DE PIÈCES JUSTIFICATIVES
          </h1>
          <p className="text-slate-600 max-w-lg mx-auto">
            Téléversez vos documents pour compléter votre dossier de financement.
            Recherchez votre dossier avec la référence de votre devis ou votre adresse email.
          </p>
        </div>

        {/* Search Section */}
        <Card className="mb-8 border-2 border-purple-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-purple-600" />
              Rechercher votre dossier
            </CardTitle>
            <CardDescription>
              Entrez la référence de votre devis (ex: DEV-2024-0001) ou l'email utilisé lors de la demande
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Search type selection */}
              <div className="flex gap-2">
                <Button
                  variant={searchType === 'reference' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSearchType('reference')}
                  className={searchType === 'reference' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Par référence devis
                </Button>
                <Button
                  variant={searchType === 'email' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSearchType('email')}
                  className={searchType === 'email' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Par email
                </Button>
              </div>

              {/* Search input */}
              <div className="flex gap-2">
                <Input
                  type={searchType === 'email' ? 'email' : 'text'}
                  placeholder={searchType === 'reference' ? 'DEV-2024-0001' : 'votre@email.com'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                  data-testid="financing-search-input"
                />
                <Button
                  onClick={handleSearch}
                  disabled={searching}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="financing-search-btn"
                >
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {searchError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {searchError}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        {financingRequest && (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* Client & Devis Info */}
            <Card>
              <CardHeader className="bg-slate-50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Informations du dossier</CardTitle>
                  <Badge className={
                    financingRequest.status === 'accepte' ? 'bg-green-100 text-green-700' :
                    financingRequest.status === 'refuse' ? 'bg-red-100 text-red-700' :
                    financingRequest.status === 'envoye_banque' ? 'bg-purple-100 text-purple-700' :
                    financingRequest.status === 'dossier_complet' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>
                    {financingRequest.status === 'en_cours' && 'En cours'}
                    {financingRequest.status === 'dossier_complet' && 'Dossier complet'}
                    {financingRequest.status === 'envoye_banque' && 'Envoyé à la banque'}
                    {financingRequest.status === 'accepte' && 'Accepté'}
                    {financingRequest.status === 'refuse' && 'Refusé'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4" />
                      <span className="font-medium">{clientInfo?.name}</span>
                    </div>
                    {clientInfo?.email && (
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <Mail className="w-4 h-4" />
                        {clientInfo.email}
                      </div>
                    )}
                    {clientInfo?.phone && (
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <Phone className="w-4 h-4" />
                        {clientInfo.phone}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {devisInfo && (
                      <>
                        <div className="flex items-center gap-2 text-slate-600">
                          <FileText className="w-4 h-4" />
                          <span className="font-medium">Devis {devisInfo.reference}</span>
                        </div>
                        <div className="text-sm text-slate-500">
                          {devisInfo.mini_pelle_name}
                        </div>
                        <div className="text-lg font-bold text-purple-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devisInfo.total || 0)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Progress Bar */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Progression du dossier</span>
                  <span className="text-sm font-bold text-purple-600">{progress}%</span>
                </div>
                <div className="w-full bg-purple-100 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                {progress === 100 && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Dossier complet ! Notre équipe va le traiter rapidement.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Documents List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Pièces justificatives à fournir
                </CardTitle>
                <CardDescription>
                  Cliquez sur "Téléverser" pour ajouter chaque document. Les formats acceptés sont : PDF, JPG, PNG.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {financingRequest.documents?.map((doc) => {
                    const statusInfo = getStatusInfo(doc.status);
                    const StatusIcon = statusInfo.icon;
                    
                    return (
                      <div
                        key={doc.doc_id}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                          doc.status === 'validated' ? 'border-green-200 bg-green-50' :
                          doc.status === 'uploaded' ? 'border-blue-200 bg-blue-50' :
                          doc.status === 'rejected' ? 'border-red-200 bg-red-50' :
                          'border-slate-200 bg-white hover:border-purple-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            doc.status === 'validated' ? 'bg-green-100 text-green-600' :
                            doc.status === 'uploaded' ? 'bg-blue-100 text-blue-600' :
                            doc.status === 'rejected' ? 'bg-red-100 text-red-600' :
                            'bg-slate-100 text-slate-400'
                          }`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {doc.name}
                              {doc.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            <p className="text-sm text-slate-500">
                              {doc.status === 'pending' && 'Document non fourni'}
                              {doc.status === 'uploaded' && `Téléversé : ${doc.file_name || 'fichier'}`}
                              {doc.status === 'validated' && 'Document validé ✓'}
                              {doc.status === 'rejected' && (
                                <span className="text-red-600">
                                  Rejeté {doc.rejection_reason && `- ${doc.rejection_reason}`}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                          
                          {(doc.status === 'pending' || doc.status === 'rejected') && (
                            <>
                              <input
                                type="file"
                                id={`file-${doc.doc_id}`}
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleUpload(doc.doc_id, file);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => document.getElementById(`file-${doc.doc_id}`)?.click()}
                                disabled={uploadingDocId === doc.doc_id}
                                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                                data-testid={`upload-btn-${doc.doc_id}`}
                              >
                                {uploadingDocId === doc.doc_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-1" />
                                    Téléverser
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2 font-medium">Légende :</p>
                  <div className="flex flex-wrap gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-slate-300" /> En attente
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-blue-500" /> Téléversé
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-500" /> Validé
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-red-500" /> Rejeté (à refournir)
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    * Documents obligatoires
                  </p>
                </div>

                {/* Submit Button */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  {submitted || financingRequest.status === 'dossier_complet' || financingRequest.status === 'envoye_banque' || financingRequest.status === 'accepte' ? (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-green-700 mb-2">
                        Dossier soumis avec succès !
                      </h3>
                      <p className="text-green-600 text-sm">
                        Notre équipe a été notifiée et va traiter votre demande dans les plus brefs délais.
                        Vous recevrez un email de confirmation.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Button
                        size="lg"
                        onClick={handleSubmitDossier}
                        disabled={!allRequiredDocsUploaded() || submitting}
                        className={`px-8 py-6 text-lg font-semibold transition-all ${
                          allRequiredDocsUploaded()
                            ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50'
                            : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        }`}
                        data-testid="submit-dossier-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Soumettre mon dossier complet
                          </>
                        )}
                      </Button>
                      {!allRequiredDocsUploaded() && (
                        <p className="text-sm text-amber-600 mt-3 flex items-center justify-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Veuillez téléverser tous les documents obligatoires (*) pour activer ce bouton
                        </p>
                      )}
                      {allRequiredDocsUploaded() && (
                        <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Tous les documents obligatoires sont fournis. Vous pouvez soumettre votre dossier !
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Help Section */}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800 mb-1">Besoin d'aide ?</h3>
                    <p className="text-sm text-slate-600">
                      Si vous avez des questions concernant les documents à fournir ou le processus de financement, 
                      n'hésitez pas à nous contacter par email ou par téléphone. Notre équipe est là pour vous accompagner.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Initial state - No search yet */}
        {!financingRequest && !searchError && !searching && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              Recherchez votre dossier pour commencer
            </h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Utilisez la référence de votre devis (que vous avez reçue par email) ou votre adresse email pour accéder à votre dossier de financement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
